import { useEffect, useState } from "react";
import styles from "./OverviewTab.module.scss";
import classNames from "classnames/bind";
import { PencilIcon, PencilV2Icon, PlusIcon } from "~/assets/icons";
import LoadingMainContent from "~/components/common/loading-main-content/LoadingMainContent";
import EditJobModal from "./component/edit-job-details/EditJobModal";
import AddAssigneesModal from "./component/add-assognees/AddAssigneesModal";
import userManagementApi, { ISalesRep } from "~/apis/userManagement";
import jobApi, { ITradeType } from "~/apis/job";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import {
  handleGetOverviewActivityFeed,
  handleGetOverviewCustomerDetails,
  handleGetOverviewJobDetails,
  handleUpdateOverviewCustomerDetails,
  handleUpdateOverviewJobDetails,
} from "~/thunks/job-details/jobDetailsThunk";
import { IPayloadUpdateOverviewCustomerDetails, IPayloadUpdateOverviewJobDetails } from "~/utils/interface/jobDetails";
import { notify } from "~/utils/helper";
import EditCustomerModal from "./component/edit-customer-details/EditCustomerModal";
import { ICustomer } from "~/apis/crm";

const cx = classNames.bind(styles);

interface OverviewTabProps {
  jobId: string;
}

type OverviewSubTab = "job-details" | "customer-details" | "activity-feed";

const subTabs = [
  { key: "job-details" as OverviewSubTab, label: "Job Details" },
  { key: "customer-details" as OverviewSubTab, label: "Customer Details" },
  { key: "activity-feed" as OverviewSubTab, label: "Activity Feed" },
];

const OverviewTab = ({ jobId }: OverviewTabProps) => {
  const dispatch = useAppDispatch();
  const [activeSubTab, setActiveSubTab] =
    useState<OverviewSubTab>("job-details");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssigneesModal, setShowAssigneesModal] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [salesReps, setSalesReps] = useState<ISalesRep[]>([]);
  const [tradeTypes, setTradeTypes] = useState<ITradeType[]>([]);
  const { overviewJobDetails, isLoadingOverviewJobDetails } = useAppSelector(
    (state) => state.jobDetails
  );
  const { overviewCustomerDetails, isLoadingOverviewCustomerDetails } =
    useAppSelector((state) => state.jobDetails);
  const { overviewActivityFeed, isLoadingOverviewActivityFeed } =
    useAppSelector((state) => state.jobDetails);
  const [editCustomerDetail, setEditCustomerDetail] =
    useState<ICustomer | null>(null);

  // Fetch overview job details
  useEffect(() => {
    dispatch(handleGetOverviewJobDetails(jobId));
    dispatch(handleGetOverviewCustomerDetails(jobId));
    dispatch(handleGetOverviewActivityFeed(jobId));
  }, [dispatch, jobId]);

  // Fetch Sales Reps and Trade Types
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch Sales Reps
        const salesRepsResponse = await userManagementApi.getSalesReps();
        if (salesRepsResponse.data?.data?.responses) {
          setSalesReps(salesRepsResponse.data.data.responses);
        }

        // Fetch Trade Types
        const tradeTypesResponse = await jobApi.getTradeTypes();
        if (tradeTypesResponse.data?.data?.responses) {
          setTradeTypes(tradeTypesResponse.data.data.responses);
        }
      } catch (error) {
        console.error("Error fetching salesReps and tradeTypes:", error);
      }
    };

    fetchData();
  }, []);

  const handleSubTabChange = (key: OverviewSubTab) => {
    setActiveSubTab(key);
  };

  const handleEditClick = () => {
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
  };

  const handleSaveEditJob = async (data: IPayloadUpdateOverviewJobDetails) => {
    try {
      await dispatch(
        handleUpdateOverviewJobDetails({ id: jobId, data })
      ).unwrap();
      notify("success", "Job updated successfully!");
      dispatch(handleGetOverviewJobDetails(jobId));
      dispatch(handleGetOverviewCustomerDetails(jobId));
      handleCloseEditModal();
    } catch (error: any) {
      notify(
        "error",
        error?.response?.data?.message ||
          error?.message ||
          "Failed to update job"
      );
    }
  };

  const handleAddAssigneesClick = () => {
    setShowAssigneesModal(true);
  };

  const handleSaveAssignees = () => {
    dispatch(handleGetOverviewJobDetails(jobId));
  };

  const handleSaveEditCustomerModal = async (contactId: string, data: IPayloadUpdateOverviewCustomerDetails) => {
    try {
      await dispatch(
        handleUpdateOverviewCustomerDetails({ id: contactId, data })
      ).unwrap();
      notify("success", "Customer updated successfully!");
      dispatch(handleGetOverviewCustomerDetails(jobId));
      setShowEditCustomerModal(false);
    } catch (error: any) {
      notify("error", `Failed to update customer: ${error?.response?.data?.message || error?.message || "Failed to update customer"}`);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getStatusClass = (status: string) => {
    if (!status) return "";
    const statusLower = status.toLowerCase().replace(/\s+/g, "-");
    return statusLower;
  };

  const getShingleColorHex = (colorValue: string | undefined): string => {
    if (!colorValue) return "#B1AB1D";

    const colorMap: Record<string, string> = {
      BLACK: "#000000",
      BROWN: "#8B4513",
      GRAY: "#808080",
      WHITE: "#FFFFFF",
      RED: "#FF0000",
      GREEN: "#00FF00",
      BLUE: "#0000FF",
    };

    return colorMap[colorValue.toUpperCase()] || "#B1AB1D";
  };

  const isPresetShingleColor = (colorValue: string | undefined) => {
    if (!colorValue) return false;
    return ["BLACK", "BROWN", "GRAY", "WHITE", "RED", "GREEN", "BLUE"].includes(
      colorValue.toUpperCase()
    );
  };

  const renderJobDetailsContent = () => {
    return (
      <div className={cx("job-details-content")}>
        <div className={cx("columns-container")}>
          {/* Column 1 */}
          <div className={cx("column")}>
            <div className={cx("detail-row")}>
              <span className={cx("label")}>Lead Source</span>
              <span className={cx("value")}>
                {overviewJobDetails?.leadSource || "N/A"}
              </span>
            </div>
            <div className={cx("detail-row")}>
              <span className={cx("label")}>Name of Referral</span>
              <span className={cx("value")}>
                {overviewJobDetails?.referralName || "N/A"}
              </span>
            </div>
            <div className={cx("detail-row")}>
              <span className={cx("label")}>Shingle</span>
              <div className={cx("shingle-indicator")}>
                <div
                  className={cx("shingle-color")}
                  style={{
                    backgroundColor: getShingleColorHex(
                      overviewJobDetails?.color
                    ),
                  }}
                />
                {overviewJobDetails?.color &&
                  !isPresetShingleColor(overviewJobDetails.color) && (
                    <span className={cx("value")}>{overviewJobDetails.color}</span>
                  )}
              </div>
            </div>
            <div className={cx("detail-row")}>
              <span className={cx("label")}>Trade Type</span>
              <span className={cx("value")}>
                {overviewJobDetails?.tradeTypes
                  ?.map((trade) => trade.name)
                  .join(", ") || "N/A"}
              </span>
            </div>
            <div className={cx("detail-row")}>
              <span className={cx("label")}>Status</span>
              <div
                className={cx(
                  "status-badge",
                  getStatusClass(overviewJobDetails?.status || "")
                )}
              >
                {overviewJobDetails?.status || "N/A"}
              </div>
            </div>
          </div>

          {/* Column 2 */}
          <div className={cx("column")}>
            <div className={cx("detail-row")}>
              <span className={cx("label")}>Type</span>
              <span className={cx("value")}>
                {overviewJobDetails?.jobType || "N/A"}
              </span>
            </div>
            <div className={cx("detail-row")}>
              <span className={cx("label")}># of squares</span>
              <span className={cx("value")}>
                {`${overviewJobDetails?.numberOfSquares} Sq ft` || "N/A"}
              </span>
            </div>
            <div className={cx("detail-row")}>
              <span className={cx("label")}>Pitch</span>
              <span className={cx("value")}>
                {`${overviewJobDetails?.pitch} Sq ft` || "N/A"}
              </span>
            </div>
            <div className={cx("detail-row")}>
              <span className={cx("label")}>Sales Rep</span>
              <span className={cx("value")}>
                {salesReps.find(
                  (salesRep) =>
                    String(salesRep.id) ===
                    String(overviewJobDetails?.salesRep?.id)
                )?.username || "N/A"}
              </span>
            </div>
            <div className={cx("detail-row")}>
              <span className={cx("label")}>Project Value</span>
              <span className={cx("value")}>
                {overviewJobDetails?.estimates && overviewJobDetails.estimates.length > 0
                  ? overviewJobDetails.estimates
                      .reduce(
                        (acc, estimate) => acc + (parseFloat(estimate.totalAmount) || 0),
                        0
                      )
                      .toLocaleString("en-US", { style: "currency", currency: "USD" })
                  : "$0"}
              </span>
            </div>
          </div>

          {/* Column 3 */}
          <div className={cx("column")}>
            <div className={cx("detail-row")}>
              <span className={cx("label")}>Created By</span>
              <span className={cx("value")}>
                {overviewJobDetails?.createdByAccount?.profile?.firstName &&
                  overviewJobDetails?.createdByAccount?.profile?.lastName
                  ? `${overviewJobDetails.createdByAccount.profile.firstName} ${overviewJobDetails.createdByAccount.profile.lastName}`
                  : "N/A"}{" "}
                • {formatDate(overviewJobDetails?.createdAt || "")}
              </span>
            </div>
            <div className={cx("detail-row")}>
              <span className={cx("label")}>Start Date</span>
              <span className={cx("value")}>
                {formatDate(overviewJobDetails?.createdAt || "")}
              </span>
            </div>
            <div className={cx("detail-row")}>
              <span className={cx("label")}>Shingle Manufacturer</span>
              <span className={cx("value")}>
                {overviewJobDetails?.shingleManufacturer || "N/A"}
              </span>
            </div>
            <div className={cx("detail-row")}>
              <span className={cx("label")}>Assigned Crew</span>
              <span className={cx("value")}>
                {overviewJobDetails?.teamMembers?.length && overviewJobDetails?.teamMembers?.length > 0
                  ? overviewJobDetails?.teamMembers
                    ?.map((teamMember) => {
                      const profile = teamMember.account?.profile;
                      if (profile?.firstName || profile?.lastName) {
                        return [profile.firstName, profile.lastName].filter(Boolean).join(" ");
                      }
                      return null;
                    })
                    .filter(Boolean)
                    .join(", ")
                  : "N/A"}
              </span>
            </div>
          </div>
        </div>

        {/* Project Description Card */}
        <div className={cx("project-description-card")}>
          <h3 className={cx("project-description-title")}>
            Project Description
          </h3>
          <p className={cx("project-description-text")}>
            {overviewJobDetails?.description || "N/A"}
          </p>
        </div>

        {/* Insurance Information and Assigned Team Members Cards */}
        <div className={cx("cards-row")}>
          {/* Insurance Information Card */}
          {overviewJobDetails?.jobType === 'Insurance' && (
            <div className={cx("info-card", "insurance-info-card")}>
              <h3 className={cx("card-title")}>Insurance Information</h3>
              <div className={cx("card-content")}>
                <div className={cx("info-item")}>
                  <span className={cx("info-label")}>Company Name</span>
                  <span className={cx("info-value")}>
                    {overviewJobDetails?.insuranceDetail?.companyName || "N/A"}
                  </span>
                </div>
                <div className={cx("info-item")}>
                  <span className={cx("info-label")}>Claim #</span>
                  <span
                    className={cx("info-value")}
                    title={overviewJobDetails?.insuranceDetail?.claimNumber || undefined}
                  >
                    {overviewJobDetails?.insuranceDetail?.claimNumber || "N/A"}
                  </span>
                </div>
                <div className={cx("info-item")}>
                  <span className={cx("info-label")}>Email Address</span>
                  <span
                    className={cx("info-value")}
                    title={overviewJobDetails?.insuranceDetail?.email || undefined}
                  >
                    {overviewJobDetails?.insuranceDetail?.email || "N/A"}
                  </span>
                </div>
                <div className={cx("info-item")}>
                  <span className={cx("info-label")}>Adjuster Name</span>
                  <span className={cx("info-value")}>
                    {overviewJobDetails?.insuranceDetail?.adjusterName || "N/A"}
                  </span>
                </div>
                <div className={cx("info-item")}>
                  <span className={cx("info-label")}>Policy Type</span>
                  <span className={cx("info-value")}>
                    {overviewJobDetails?.insuranceDetail?.policyType || "N/A"}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Assigned Team Members Card */}
          <div className={cx("info-card", "assigned-team-members-card")}>
            <div className={cx("card-header")}>
              <h3 className={cx("card-title")}>Assigned Team Members</h3>

              <PrimaryButton
                title="Add Assignees"
                onClick={handleAddAssigneesClick}
                icon={<PlusIcon className={cx("add-icon")} />}
                className={cx("add-assignees-button")}
              />
            </div>
            <div className={cx("card-content")}>
              {overviewJobDetails?.teamMembers?.length &&
                overviewJobDetails?.teamMembers?.length > 0 ? (
                <div className={cx("team-members-list")}>
                  {overviewJobDetails?.teamMembers?.map((teamMember) => (
                    <div className={cx("team-member-item")} key={teamMember.id}>
                      <div className={cx("member-info-container")}>
                        <div className={cx("member-avatar")}>
                          <span className={cx("avatar-text")}>
                            {teamMember.account?.profile?.firstName?.charAt(0)}
                            {teamMember.account?.profile?.lastName?.charAt(0)}
                          </span>
                        </div>
                        <div className={cx("member-info")}>
                          <span className={cx("member-name")}>
                            {teamMember.account?.profile?.firstName}{" "}
                            {teamMember.account?.profile?.lastName}
                          </span>
                        </div>
                      </div>
                      <div className={cx("member-role")}>
                        {teamMember.roleText}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={cx("no-team-members")}>
                  <p>No team members assigned</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCustomerDetailsContent = () => {
    const handleEditContact = (customer: ICustomer) => {
      setShowEditCustomerModal(true);
      setEditCustomerDetail(customer);
    };

    return (
      <div className={cx("customer-details-content")}>
        {overviewCustomerDetails?.primaryCustomer && (
          <div className={cx("contact-card")}>
            <div className={cx("contact-header")}>
              <div className={cx("contact-info")}>
                <div className={cx("contact-avatar")}>
                  <span className={cx("avatar-initials")}>
                    {overviewCustomerDetails.primaryCustomer?.name?.charAt(0) ||
                      "N/A"}
                  </span>
                </div>
                <div className={cx("contact-name-section")}>
                  <h3 className={cx("contact-name")}>
                    {overviewCustomerDetails.primaryCustomer?.name}
                  </h3>
                  <p className={cx("contact-type")}>Primary Contact</p>
                </div>
              </div>

              <PrimaryButton
                title="Edit"
                onClick={() =>
                  overviewCustomerDetails?.primaryCustomer &&
                  handleEditContact(
                    overviewCustomerDetails.primaryCustomer as ICustomer
                  )
                }
                icon={<PencilIcon className={cx("edit-contact-icon")} />}
                className={cx("edit-contact-button")}
              />
            </div>

            <div className={cx("contact-details")}>
              <div className={cx("contact-details-column")}>
                <div className={cx("contact-detail-item")}>
                  <span className={cx("contact-label")}>Phone</span>
                  <span className={cx("contact-value")}>
                    {overviewCustomerDetails.primaryCustomer?.primaryPhone}
                  </span>
                </div>
                <div className={cx("contact-detail-item")}>
                  <span className={cx("contact-label")}>Referred By</span>
                  <span className={cx("contact-value")}>
                    {overviewCustomerDetails.primaryCustomer?.referredBy}
                  </span>
                </div>
              </div>

              <div className={cx("contact-details-column")}>
                <div className={cx("contact-detail-item")}>
                  <span className={cx("contact-label")}>Email</span>
                  <span className={cx("contact-value")}>
                    {overviewCustomerDetails.primaryCustomer?.primaryEmail}
                  </span>
                </div>
                <div className={cx("contact-detail-item")}>
                  <span className={cx("contact-label")}>Customer Since</span>
                  <span className={cx("contact-value")}>
                    {formatDate(
                      overviewCustomerDetails.primaryCustomer?.createdAt || ""
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        {overviewCustomerDetails?.secondaryCustomer && (
          <div className={cx("contact-card")}>
            <div className={cx("contact-header")}>
              <div className={cx("contact-info")}>
                <div className={cx("contact-avatar")}>
                  <span className={cx("avatar-initials")}>
                    {overviewCustomerDetails.secondaryCustomer?.name?.charAt(
                      0
                    ) || "N/A"}
                  </span>
                </div>
                <div className={cx("contact-name-section")}>
                  <h3 className={cx("contact-name")}>
                    {overviewCustomerDetails.secondaryCustomer?.name}
                  </h3>
                  <p className={cx("contact-type")}>Primary Contact</p>
                </div>
              </div>

              <PrimaryButton
                title="Edit"
                onClick={() =>
                  overviewCustomerDetails?.secondaryCustomer &&
                  handleEditContact(
                    overviewCustomerDetails.secondaryCustomer as ICustomer
                  )
                }
                icon={<PencilIcon className={cx("edit-contact-icon")} />}
                className={cx("edit-contact-button")}
              />
            </div>

            <div className={cx("contact-details")}>
              <div className={cx("contact-details-column")}>
                <div className={cx("contact-detail-item")}>
                  <span className={cx("contact-label")}>Phone</span>
                  <span className={cx("contact-value")}>
                    {overviewCustomerDetails.secondaryCustomer?.primaryPhone}
                  </span>
                </div>
                <div className={cx("contact-detail-item")}>
                  <span className={cx("contact-label")}>Referred By</span>
                  <span className={cx("contact-value")}>
                    {overviewCustomerDetails.secondaryCustomer?.referredBy}
                  </span>
                </div>
              </div>

              <div className={cx("contact-details-column")}>
                <div className={cx("contact-detail-item")}>
                  <span className={cx("contact-label")}>Email</span>
                  <span className={cx("contact-value")}>
                    {overviewCustomerDetails.secondaryCustomer?.primaryEmail}
                  </span>
                </div>
                <div className={cx("contact-detail-item")}>
                  <span className={cx("contact-label")}>Customer Since</span>
                  <span className={cx("contact-value")}>
                    {formatDate(
                      overviewCustomerDetails.secondaryCustomer?.createdAt || ""
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className={cx("billing-details-card")}>
          <h3 className={cx("card-title")}>Billing Details</h3>

          <div className={cx("billing-details-content")}>
            <div className={cx("billing-detail-item")}>
              <span className={cx("billing-detail-label")}>Payment Terms</span>
              <span className={cx("billing-detail-value")}>
                {overviewCustomerDetails?.paymentTerms || "N/A"}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderActivityFeedContent = () => {
    const getInitials = (activity: any) => {
      const firstInitial =
        activity.createdByAccount?.profile?.firstName?.charAt(0) || "";
      const lastInitial =
        activity.createdByAccount?.profile?.lastName?.charAt(0) || "";

      if (!firstInitial && !lastInitial) return "N/A";

      return `${firstInitial}${lastInitial}`.toUpperCase();
    };

    const getFullName = (activity: any) => {
      const firstName = activity.createdByAccount?.profile?.firstName || "";
      const lastName = activity.createdByAccount?.profile?.lastName || "";

      if (!firstName && !lastName) return "N/A";

      return `${firstName} ${lastName}`.trim();
    };

    const formatTimeAgo = (dateString?: string) => {
      if (!dateString) return "";

      const createdAt = new Date(dateString).getTime();
      const now = Date.now();

      const diffMs = now - createdAt;
      if (diffMs < 0) return "just now";

      const seconds = Math.floor(diffMs / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);
      const weeks = Math.floor(days / 7);
      const months = Math.floor(days / 30);
      const years = Math.floor(days / 365);

      if (years > 0) return `${years} year${years > 1 ? "s" : ""} ago`;
      if (months > 0) return `${months} month${months > 1 ? "s" : ""} ago`;
      if (weeks > 0) return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
      if (days > 0) return `${days} day${days > 1 ? "s" : ""} ago`;
      if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
      if (minutes > 0) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;

      return "just now";
    };

    return (
      <div className={cx("activity-feed-content")}>
        {overviewActivityFeed?.map((activity) => (
          <div key={activity.id} className={cx("activity-item")}>
            <div className={cx("activity-avatar")}>
              <span className={cx("activity-avatar-initials")}>
                {getInitials(activity)}
              </span>
            </div>

            <div className={cx("activity-details")}>
              <div className={cx("activity-header")}>
                <span className={cx("activity-user-name")}>
                  {getFullName(activity)}
                </span>
                <span className={cx("activity-action")}>{activity.title}</span>
                <span className={cx("activity-time")}>
                  {formatTimeAgo(activity.createdAt)}
                </span>
              </div>
              <p className={cx("activity-description")}>
                {activity.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeSubTab) {
      case "job-details":
        return renderJobDetailsContent();
      case "customer-details":
        return renderCustomerDetailsContent();
      case "activity-feed":
        return renderActivityFeedContent();
      default:
        return null;
    }
  };

  if (isLoadingOverviewJobDetails || isLoadingOverviewCustomerDetails || isLoadingOverviewActivityFeed) {
    return (
      <div className={cx("overview-tab-container")}>
        <LoadingMainContent />
      </div>
    );
  }

  return (
    <div className={cx("overview-tab-container")}>
      <div className={cx("top-navigation")}>
        <div className={cx("tabs-container")}>
          {subTabs.map((tab) => (
            <button
              key={tab.key}
              className={cx("tab-item", { active: activeSubTab === tab.key })}
              onClick={() => handleSubTabChange(tab.key)}
            >
              <h4 className={cx("tab-text")}>{tab.label}</h4>
            </button>
          ))}
        </div>
        {activeSubTab === "job-details" && (
          <PrimaryButton
            title="Edit"
            onClick={handleEditClick}
            icon={<PencilV2Icon className={cx("edit-icon")} />}
            className={cx("edit-button")}
          />
        )}
      </div>

      <div className={cx("content-area")}>{renderTabContent()}</div>

      {/* Edit Job Modal */}
      {showEditModal && (
        <EditJobModal
          isOpen={showEditModal}
          jobDetail={overviewJobDetails}
          salesReps={salesReps}
          tradeTypes={tradeTypes}
          onClose={handleCloseEditModal}
          onSave={handleSaveEditJob}
        />
      )}

      {/* Add Assignees Modal */}
      {showAssigneesModal && (
        <AddAssigneesModal
          isOpen={showAssigneesModal}
          jobId={jobId}
          existingAssignees={overviewJobDetails?.teamMembers || []}
          onClose={() => setShowAssigneesModal(false)}
          onSave={handleSaveAssignees}
        />
      )}

      {/* Edit Customer Modal */}
      {showEditCustomerModal && (
        <EditCustomerModal
          isOpen={showEditCustomerModal}
          customerDetail={editCustomerDetail as ICustomer}
          onClose={() => setShowEditCustomerModal(false)}
          onSave={(data) => handleSaveEditCustomerModal(editCustomerDetail?.id as string, data)}
        />
      )}
    </div>
  );
};

export default OverviewTab;
